import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type DataPoint = {
  time: string;
  date: string;
  fullLabel: string;
  consumption: number;
  hourIndex: number;
};

type CustomLineChartProps = {
  data: DataPoint[];
};

type CustomTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string; index: number };
  data: DataPoint[];
};

function CustomXAxisTick({ x, y, payload, data }: CustomTickProps) {
  const index = payload?.index ?? 0;
  const item = data[index];

  if (!item) return null;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="middle" fill="#666" fontSize={9}>
        {item.time}
      </text>
      <text x={0} y={0} dy={22} textAnchor="middle" fill="#999" fontSize={8}>
        {item.date}
      </text>
    </g>
  );
}

function CustomTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const item = payload[0].payload as DataPoint;
    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #ccc",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600 }}>
          {item.time} - {item.date}
        </p>
        <p style={{ margin: "4px 0 0", color: "#f04684" }}>
          Consumption: {item.consumption} L
        </p>
      </div>
    );
  }
  return null;
}

export default function CustomLineChart({ data }: CustomLineChartProps) {
  const dataLength = data.length;

  // Determine tick interval based on data length
  // Show fewer labels for larger datasets
  const getTickInterval = () => {
    if (dataLength <= 24) return 3; // Every 4 hours for 24h
    if (dataLength <= 48) return 5; // Every 6 hours for 48h
    return 11; // Every 12 hours for 96h
  };

  return (
    <ResponsiveContainer height={220} width={"100%"}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          tick={(props) => <CustomXAxisTick {...props} data={data} />}
          interval={getTickInterval()}
          height={40}
        />
        <YAxis tick={{ fontSize: 10 }} width={45} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line
          type="monotone"
          dataKey="consumption"
          stroke="#f04684"
          strokeWidth={2}
          dot={dataLength <= 48}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
